<script setup lang="ts">
import ButtonContactUs from '@/components/shared/common/ButtonContactUs.vue'
import AllPricesPriceCard from '@/components/all-prices/AllPricesPriceCard.vue'
import AllPricesTableModal from '~/components/all-prices/AllPricesTableModal.vue'
import BreadcrumbWithoutSidebar from '~/components/navigation/BreadcrumbWithoutSidebar.vue'

import { ALL_PRICES } from '~/constants/all-prices'
import type { TGeneral } from '~/src/types/common'
import { getLibNameByMarket } from '~/src/utils/all-reports'

withDefaults(
  defineProps<{
    summaries: TGeneral[]
  }>(),
  {
    summaries: () => [],
  },
)

const modalStore = useModalStore()

const breadcrumbs = [
  { name: 'Services' },
  { name: 'Price Assessments' },
  { name: 'All Prices' },
]

const heroBackgroundImage =
  'https://res.cloudinary.com/benchmarkminerals/image/upload/v1733167870/assets/all-prices-subscription/all-prices-hero_fwv2uy.png'

const selectedProductName = ref('')

const handleClick = (productName: string) => {
  selectedProductName.value = String(getLibNameByMarket(productName))
    .toLowerCase()
    .replace(/ /g, '-')
    .trim()
  modalStore.open('allPricesTable')
}
</script>

<template>
  <section class="relative py-5 md:pt-6 md:pb-28 bg-black/40">
    <img
      class="absolute w-full h-full -z-10 object-cover top-0"
      :src="heroBackgroundImage"
      alt="Hero Background"
    />
    <div class="max-w-[90rem] mx-auto min-w-0 px-4 lg:pl-10 lg:pr-6 2xl:px-0">
      <BreadcrumbWithoutSidebar :menu="breadcrumbs" class="text-white" />
      <div
        class="relative backdrop-blur-sm flex flex-col gap-y-6 text-white bg-black/5 max-w-[896px]"
      >
        <h1
          class="text-h3 md:text-5xl font-bold md:mt-[83px]"
          data-testid="all-prices-hero-title"
        >
          All Prices Subscription
        </h1>
        <p
          class="md:text-lg md:font-semibold"
          data-testid="all-prices-hero-description"
        >
          Access the entire suite of >140 Benchmark prices, including exclusive
          grades, collected directly from active market participants. Prices are
          assessed to mineral-specific methodologies providing granular
          visibility across lithium ion and energy transition supply chains.
        </p>
        <ButtonContactUs class="w-full md:w-fit" text="Contact Us" />
      </div>
      <div
        class="mt-6 md:mt-10 grid md:flex flex-wrap justify-center grid-cols-2 gap-4 lg:gap-6"
      >
        <AllPricesPriceCard
          v-for="(price, i) in ALL_PRICES"
          :key="`${price.name}-${i}`"
          class="odd:bg-p-50 even:bg-p-200 hover:!bg-p-100"
          :price-name="price.name"
          :icon-name="price.icon"
          @click="handleClick(price.name)"
        />
      </div>
    </div>
    <AllPricesTableModal
      :product-name="selectedProductName"
      :summaries="summaries"
    />
  </section>
</template>
